import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { AlertTriangle, Mail, ShieldOff, ShieldCheck, Loader2, RefreshCw, History } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const FLAGS_META = {
    trial_milestones_enabled: {
        label: 'Hitos del período de exploración (día 3 · 5 · 7)',
        description: 'Correos automáticos al docente cuando cumple día 3, 5 o 7 desde el inicio de su trial. Incluye "Tu período de exploración está finalizando".',
        riesgo: 'ALTO — se dispara diariamente. Si la BD tiene fechas legacy de trial, puede llegar al docente equivocado.',
    },
    m2_reminder_enabled: {
        label: 'Recordatorio cierre Módulo 2 (11 oct 2026)',
        description: '3 días antes del cierre oficial de agendamiento M2. Se envía SOLO el 11 de octubre. Filtra docentes que ya completaron M2.',
        riesgo: 'Medio — fecha única, requiere haber tenido actividad reciente.',
    },
    cierre_reminder_enabled: {
        label: 'Recordatorio cierre Portafolio (3 nov 2026)',
        description: '1 semana antes del cierre nacional del Portafolio. Se envía SOLO el 3 de noviembre.',
        riesgo: 'Medio — fecha única, requiere haber tenido actividad reciente.',
    },
};

function formatHora(iso) {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleString('es-CL', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch (_) {
        return '—';
    }
}

export default function EmailAutomationsPanel() {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(null); // flag actualmente cambiando
    const [data, setData] = useState(null);
    const [showLog, setShowLog] = useState(false);
    const [log, setLog] = useState(null);
    const [logLoading, setLogLoading] = useState(false);

    const fetchData = async () => {
        try {
            setLoading(true);
            const { data: resp } = await api.get('/admin/email-automations');
            setData(resp);
        } catch (e) {
            toast.error('Error al cargar el estado de automatizaciones');
        } finally {
            setLoading(false);
        }
    };

    const fetchLog = async () => {
        try {
            setLogLoading(true);
            const { data: resp } = await api.get('/admin/email-automations/sent-log?days=30');
            setLog(resp);
        } catch (e) {
            toast.error('Error al cargar el log de envíos');
        } finally {
            setLogLoading(false);
        }
    };

    useEffect(() => { fetchData(); }, []);

    const handleToggle = async (flag, currentEnabled) => {
        const turningOn = !currentEnabled;
        const meta = FLAGS_META[flag];
        const confirmMsg = turningOn
            ? `Vas a ACTIVAR "${meta.label}". A partir de ahora se enviarán correos automáticamente según las reglas del scheduler. Riesgo: ${meta.riesgo}\n\n¿Confirmar?`
            : `Vas a DESACTIVAR "${meta.label}". El scheduler dejará de enviar este correo.`;
        if (!window.confirm(confirmMsg)) return;

        try {
            setSaving(flag);
            const { data: resp } = await api.put('/admin/email-automations', { flag, enabled: turningOn });
            toast.success(turningOn ? 'Automatización ACTIVADA' : 'Automatización DESACTIVADA', { duration: 4000 });
            await fetchData();
        } catch (e) {
            toast.error('No se pudo cambiar el flag');
        } finally {
            setSaving(null);
        }
    };

    if (loading) {
        return (
            <Card data-testid="email-automations-panel-loading">
                <CardContent className="flex items-center gap-3 py-8 text-slate-500">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Cargando estado de automatizaciones…
                </CardContent>
            </Card>
        );
    }

    const allOff = data && !Object.values(data.flags).some((f) => f.enabled);
    const schedulerProc = data?.scheduler_process_enabled;

    return (
        <Card data-testid="email-automations-panel">
            <CardHeader>
                <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3">
                        <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${allOff ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                            {allOff ? <ShieldCheck className="h-5 w-5" /> : <AlertTriangle className="h-5 w-5" />}
                        </div>
                        <div>
                            <CardTitle className="font-heading text-lg">Automatizaciones de correo</CardTitle>
                            <CardDescription>
                                Kill-switches por tipo de envío. Default: <strong>OFF</strong>. Activa SOLO los que hayas revisado y autorizado.
                            </CardDescription>
                        </div>
                    </div>
                    <Button variant="outline" size="sm" onClick={fetchData} className="gap-2" data-testid="refresh-automations-btn">
                        <RefreshCw className="h-3.5 w-3.5" />
                        Refrescar
                    </Button>
                </div>
            </CardHeader>
            <CardContent className="space-y-5">
                {/* Estado proceso scheduler */}
                <div className={`rounded-lg border p-3 text-sm ${schedulerProc ? 'border-amber-200 bg-amber-50/60 text-amber-900' : 'border-emerald-200 bg-emerald-50/60 text-emerald-900'}`}>
                    <div className="flex items-center gap-2">
                        {schedulerProc ? <Mail className="h-4 w-4" /> : <ShieldOff className="h-4 w-4" />}
                        <strong>Scheduler del proceso:</strong>
                        <Badge className={schedulerProc ? 'bg-amber-200 text-amber-900' : 'bg-emerald-200 text-emerald-900'}>
                            {schedulerProc ? 'ACTIVO (cron diario 13:00 UTC)' : 'DESHABILITADO (EMAIL_SCHEDULER_ENABLED=0)'}
                        </Badge>
                    </div>
                    <p className="mt-1 text-xs">
                        Aunque el proceso esté activo, cada tipo de envío respeta su flag individual. Si todos los flags están en OFF, no sale ningún correo automatizado.
                    </p>
                </div>

                {/* Flags */}
                <div className="space-y-3">
                    {Object.entries(FLAGS_META).map(([flag, meta]) => {
                        const audit = data.flags[flag] || {};
                        const enabled = !!audit.enabled;
                        return (
                            <div
                                key={flag}
                                className={`rounded-lg border p-4 transition-colors ${enabled ? 'border-amber-300 bg-amber-50/40' : 'border-slate-200 bg-white'}`}
                                data-testid={`automation-row-${flag}`}
                            >
                                <div className="flex items-start justify-between gap-4">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <h4 className="font-medium text-slate-900">{meta.label}</h4>
                                            <Badge className={enabled ? 'bg-amber-600 text-white' : 'bg-slate-200 text-slate-700'} data-testid={`automation-badge-${flag}`}>
                                                {enabled ? 'ON' : 'OFF'}
                                            </Badge>
                                        </div>
                                        <p className="mt-1 text-xs text-slate-600 leading-relaxed">{meta.description}</p>
                                        <p className="mt-1 text-[11px] text-amber-800 leading-relaxed"><strong>Riesgo:</strong> {meta.riesgo}</p>
                                        {audit.changed_at && (
                                            <p className="mt-1.5 text-[10px] text-slate-500">
                                                Último cambio: {formatHora(audit.changed_at)} · por {audit.changed_by || 'desconocido'}
                                            </p>
                                        )}
                                    </div>
                                    <div className="shrink-0 pt-1">
                                        {saving === flag ? (
                                            <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
                                        ) : (
                                            <Switch
                                                checked={enabled}
                                                onCheckedChange={() => handleToggle(flag, enabled)}
                                                data-testid={`automation-switch-${flag}`}
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Historial de envíos */}
                <div className="pt-3 border-t border-slate-100">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => { if (!showLog) fetchLog(); setShowLog((v) => !v); }}
                        className="gap-2"
                        data-testid="toggle-sent-log-btn"
                    >
                        <History className="h-4 w-4" />
                        {showLog ? 'Ocultar' : 'Ver'} historial de envíos automáticos (últimos 30 días)
                    </Button>
                    {showLog && (
                        <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50/50 p-3" data-testid="sent-log-table">
                            {logLoading ? (
                                <p className="text-xs text-slate-500 flex items-center gap-2"><Loader2 className="h-3 w-3 animate-spin" /> Cargando…</p>
                            ) : log && log.total > 0 ? (
                                <>
                                    <p className="text-xs text-slate-600 mb-2">
                                        <strong>{log.total}</strong> correos automatizados enviados en los últimos {log.window_days} días.{' '}
                                        Por template: {Object.entries(log.by_template).map(([t, n]) => `${t}=${n}`).join(' · ')}
                                    </p>
                                    <div className="max-h-72 overflow-auto">
                                        <table className="w-full text-[11px]">
                                            <thead className="text-slate-500 uppercase tracking-wide sticky top-0 bg-slate-50">
                                                <tr>
                                                    <th className="text-left px-2 py-1">Fecha</th>
                                                    <th className="text-left px-2 py-1">Template</th>
                                                    <th className="text-left px-2 py-1">Destinatario</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {log.rows.map((r, i) => (
                                                    <tr key={i} className="border-t border-slate-200">
                                                        <td className="px-2 py-1 font-mono text-slate-700">{formatHora(r.sent_at)}</td>
                                                        <td className="px-2 py-1"><Badge variant="outline" className="text-[10px]">{r.template_id}</Badge></td>
                                                        <td className="px-2 py-1 text-slate-700">{Array.isArray(r.to) ? r.to.join(', ') : r.to}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </>
                            ) : (
                                <p className="text-xs text-emerald-700 flex items-center gap-2">
                                    <ShieldCheck className="h-3 w-3" />
                                    Sin envíos automatizados en los últimos 30 días.
                                </p>
                            )}
                        </div>
                    )}
                </div>
            </CardContent>
        </Card>
    );
}
